import DatePicker from "@edaz/edazkit/DatePicker";
import type { CommonInputProps } from "@frui.ts/bootstrap/dist/controls/commonInputProps";
import { bound } from "@frui.ts/helpers";
import { getValidationMessage } from "@frui.ts/validation";
import type { IBindingProps } from "@frui.ts/views";
import { BindingComponent } from "@frui.ts/views";
import classnames from "classnames";
import { format, utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import localizationProvider from "localization/localizationProvider";
import { Observer } from "mobx-react-lite";
import React from "react";
import type { FormControlProps } from "react-bootstrap";
import { Form } from "react-bootstrap";
import { czechTimeZone, endOfCzechDay } from "utils/dateUtils";
import type { Modifier } from "react-day-picker";
import { formatDate, formatDateTime, translateGeneral } from "utils/localizations";

export interface DatePickerBoxProps {
  fromMonth?: Date;
  toMonth?: Date;
  withoutIcon?: boolean;
  endOfTheDay?: boolean;
  disabledShowTime?: boolean;
  hideToday?: boolean;
  getInputValue?: (input: any) => void;
  placementOverlay?: string;
  strategyOverlay?: string;
  disabledDays?: Modifier | Modifier[];
}

export default class DatePickerBox<TTarget> extends BindingComponent<
  TTarget,
  DatePickerBoxProps & FormControlProps & CommonInputProps & IBindingProps<TTarget>
> {
  state = {
    invalidDate: false,
  };

  protected handleValueChanged = (formattedDate: string, date?: Date, inputValue?: string) => {
    if (!formattedDate && inputValue) {
      this.setState({ invalidDate: true });
    } else {
      this.setState({ invalidDate: false });
    }

    let czechDate;
    if (date instanceof Date) {
      czechDate = zonedTimeToUtc(format(date, "yyyy-MM-dd"), czechTimeZone);

      if (this.props.endOfTheDay) {
        czechDate = endOfCzechDay(czechDate);
      }
    }
    this.setValue(czechDate);
  };

  formatValue(date: any) {
    if (date instanceof Date) {
      return utcToZonedTime(date, czechTimeZone);
    } else {
      return date;
    }
  }

  get invalidDateMessage() {
    if (this.state.invalidDate) {
      return localizationProvider.translateGeneral("invalidDate");
    }

    return undefined;
  }

  @bound protected renderInner() {
    const { target, property } = this.props as { target: TTarget; property: keyof TTarget & string }; // the cast is a TS compiler workaround
    const { noValidation, errorMessage, disabled, disabledShowTime, hideToday } = this.inheritedProps;
    const validationError =
      noValidation !== true && (this.invalidDateMessage || errorMessage || getValidationMessage(target, property));
    const placeholder = this.props.placeholder ?? translateGeneral("datepicker.placeholder");
    let datePickerComponent;
    if (this.props.getInputValue) {
      this.props.getInputValue(this.state.invalidDate);
    }

    if (disabled) {
      datePickerComponent = (
        <input
          disabled={true}
          className={classnames("form-control", { "is-invalid": validationError })}
          placeholder={placeholder}
          value={disabledShowTime ? formatDateTime(this.value) : formatDate(this.value)}
        />
      );
    } else {
      datePickerComponent = (
        <DatePicker
          localization={localizationProvider.datePickerLocalization}
          {...this.props}
          strategyOverlay={this.props.strategyOverlay}
          disabledDays={this.props.disabledDays}
          placementOverlay={this.props.placementOverlay}
          hideToday={hideToday}
          onChange={this.handleValueChanged}
          value={this.formatValue(this.value)}
          className={validationError ? "is-invalid" : ""}
          hideOnDayClick={true}
          withoutIcon={this.props.withoutIcon}
          disabled={disabled}
          placeholder={placeholder}
        />
      );
    }

    return (
      <>
        {datePickerComponent}
        {validationError && (
          <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
            {validationError}
          </Form.Control.Feedback>
        )}
      </>
    );
  }

  render() {
    return <Observer render={this.renderInner} />;
  }
}
